/**
 * MeetingAttendeeService
 * Service for MeetingAttendee entity operations
 * Route: api/meetingattendees
 * Controller: MeetingAttendeesController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * MeetingAttendeeService
 * Handles meeting attendee CRUD operations and custom endpoints
 * Maps to /api/meetingattendees endpoint
 */
export class MeetingAttendeeService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MeetingAttendeeService.DEFAULT_SERVICE_PATH,
            basePath: MeetingAttendeeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get attendee by composite key (MeetingID and UserID)
     * GET /api/meetingattendees/{meetingId}/{userId}
     * @param meetingId - Meeting ID
     * @param userId - User ID
     * @returns Meeting attendee
     */
    async getByCompositeKey(meetingId, userId) {
        const response = await this.getByCompositeKeyRaw(meetingId, userId);
        if (!response.data) {
            throw new Error(`Attendee for meeting ${meetingId} and user ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get attendee by composite key (raw)
     * @param meetingId - Meeting ID
     * @param userId - User ID
     * @returns ResponseData with meeting attendee
     */
    getByCompositeKeyRaw(meetingId, userId) {
        const endpoint = this.getEndpoint('byCompositeKey', `${meetingId}/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all attendees for a specific meeting
     * GET /api/meetingattendees/meeting/{meetingId}
     * @param meetingId - Meeting ID
     * @returns Array of meeting attendees
     */
    async getByMeetingId(meetingId) {
        const response = await this.getByMeetingIdRaw(meetingId);
        return response.data ?? [];
    }
    /**
     * Get attendees by meeting ID (raw)
     * @param meetingId - Meeting ID
     * @returns ResponseData with array of meeting attendees
     */
    getByMeetingIdRaw(meetingId) {
        const endpoint = this.getEndpoint('byMeetingId', `meeting/${meetingId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all meetings for a specific user
     * GET /api/meetingattendees/user/{userId}
     * @param userId - User ID
     * @returns Array of meeting attendees
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get meetings by user ID (raw)
     * @param userId - User ID
     * @returns ResponseData with array of meeting attendees
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('byUserId', `user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
MeetingAttendeeService.DEFAULT_SERVICE_PATH = 'meetings';
MeetingAttendeeService.BASE_PATH = 'api/meetingattendees';
//# sourceMappingURL=MeetingAttendeeService.js.map